// HotX Console 0.1.107 套餐接入IP预览；实际修改时间：2026-09-16 11:27:15 CST；精确父基线：ededcab95716ac1a5dced24f1c7bf368ce280467。
// 套餐页按三类模型展示；域名删除倒计时与历史只消费同一认证概览返回的服务端事实。
import { ControlPlaneApiError } from '@hotx-cdn/shared'
import { httpClient } from './client'
import type {
  DomainPackageSelection,
  DomainPlanPartition,
  DomainPlanPartitionCatalogItem
} from '../features/domain-plan/domainPlanPartitions'

export interface PointSummary {
  balance: number
  overdraftLimit: number
  spendingFrozen: boolean
  totalEarned: number
  totalSpent: number
  earnedRecharge: number
  earnedSignIn: number
  earnedNewcomer: number
  earnedAdminGift: number
  newcomerRewardPending: boolean
  todaySigned: boolean
  streakDays: number
}

export interface SignInStatus {
  today: string
  todaySigned: boolean
  streakDays: number
  newcomerRewardPending: boolean
  newcomerReward: number
  week: SignInDay[]
}

export interface SignInDay {
  date: string
  signed: boolean
  today: boolean
  pointStatus?: 'EARNED' | 'ESTIMATED' | 'MISSED'
  pointAmount?: number
}

export interface SignInResult {
  rewards: Array<{ sourceType: string; amount: number }>
  totalReward: number
  streakDays: number
  summary: PointSummary
  status: SignInStatus
}

export interface PointLedgerItem {
  id: number
  txnId: string
  direction: 'CREDIT' | 'DEBIT'
  sourceType: string
  amount: number
  balanceBefore: number
  balanceAfter: number
  remark?: string
  description?: string
  createdAt: string
}

export interface PlanItem {
  id: number
  planCode: string
  planName: string
  description?: string
  pointPrice: number
  periodMode: string
  saleMode: string
  autoRenewDefault: boolean
  enabled: boolean
  priceUpdatedAt?: string
  rootDomainLimit?: number
  defaultRootDomainLimit?: number
}

export interface CurrentPlan {
  customerId?: number
  customerPlanId: number
  planId: number
  planCode?: string
  planName: string
  description?: string
  pointPrice: number
  periodMode: string
  saleMode: string
  status: string
  startAt: string
  expireAt: string
  autoRenew: boolean
  periodCount: number
  selectionRequired: boolean
  defaultRootDomainLimit?: number
}

export interface ActivePushPlan {
  assignmentId: number
  planId: number
  planName: string
  assignedAt: string
  expireAt?: string | null
  graceUntil?: string | null
  expired?: boolean
  inGrace?: boolean
  accelerationPaused?: boolean
  serviceContinuesAfterExpiry?: boolean
  remark?: string
}

export interface PendingPlan {
  id: number
  currentPlanId: number
  targetPlanId: number
  targetPlanName: string
  targetPriceSnapshot: number
  periodCount: number
  expectedSwitchAt: string
  status: string
  failureCode?: string
}

export interface PlanPricing {
  discountEnabled: boolean
  tierOneMinPeriods: number
  tierOneRate: number
  tierTwoMinPeriods: number
  tierTwoRate: number
}

export interface PlanCatalog {
  currentPlan?: CurrentPlan | null
  activePush?: ActivePushPlan | null
  plans: PlanItem[]
  pendingSelection?: PendingPlan | null
  selectionRequired: boolean
  addOns: AddOnItem[]
  pricing: PlanPricing
}

export interface AddOnItem {
  id: number
  addonCode: string
  addonName: string
  description?: string
  pointPrice: number
  billingMode: string
  periodMode: string
  enabled?: boolean
}

export interface ConsumptionOrder {
  id: number
  orderId: string
  orderType: string
  productRef: string
  periods?: number
  periodCount?: number
  unitPoints?: number
  grossPoints?: number
  discountRate?: number
  discountPoints?: number
  payablePoints?: number
  status: 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'REVERSED'
  failureCode?: string
  failureMessage?: string
  createdAt: string
}

export interface InboxNotification {
  id: number
  notificationId: string
  eventType: string
  title: string
  content: string
  actionPath?: string
  referenceType?: string
  referenceId?: string
  readAt?: string | null
  createdAt: string
}

const pointErrorMessages: Record<string, string> = {
  POINTS_INSUFFICIENT: '积分不足，请前往充值中心补充积分。',
  INSUFFICIENT_POINTS: '积分不足，请前往充值中心补充积分。',
  ADMIN_PUSH_ACTIVE: '专属定制套餐生效期间，不能切换免费套餐或购买套餐。',
  OVERDRAFT_LIMIT_EXCEEDED: '本次消费会超出可用额度，请补充积分或选择其他套餐。',
  POINT_SPENDING_FROZEN: '积分消费已冻结，暂时无法执行扣积分操作。',
  ACCOUNT_DISABLED: '当前账号不可执行此操作。',
  DUPLICATE_SIGN_IN: '今日已签到，请明天再来。',
  ORDER_PROCESSING: '订单正在处理中，请勿重复提交。',
  ORDER_ALREADY_COMPLETED: '该订单已完成，无需重复提交。',
  PENDING_PLAN_NOT_FOUND: '待生效套餐不存在，请刷新后重试。',
  PENDING_PLAN_EXECUTING: '待生效套餐正在处理，暂时不能修改。',
  TARGET_PLAN_UNAVAILABLE: '目标套餐当前不可购买，请重新选择。',
  PLAN_ALREADY_EXPIRED: '当前套餐已过期，请刷新后续费或重新选择。'
}

export function pointErrorMessage(error: unknown, fallback: string) {
  if (error instanceof ControlPlaneApiError) {
    return pointErrorMessages[error.code] || fallback
  }
  return fallback
}

export interface PagedResponse<T> {
  items: T[]
  total: number
  page?: number
  size?: number
}

export interface NotificationPagedResponse extends PagedResponse<InboxNotification> {
  unreadCount: number
}

function categoryFromDirection(direction: string) {
  const value = direction.trim().toUpperCase()
  if (value === 'CREDIT') {
    return 'INCOME'
  }
  if (value === 'DEBIT') {
    return 'SPEND'
  }
  return value
}

function normalizeLedgerParams(params: Record<string, unknown>) {
  return Object.entries(params).reduce<Record<string, unknown>>((acc, [key, value]) => {
    if (value === undefined || value === null || value === '') {
      return acc
    }
    if (key === 'direction' && typeof value === 'string') {
      acc.category = categoryFromDirection(value)
      return acc
    }
    acc[key] = value
    return acc
  }, {})
}

// Per-domain package lifecycle API, updated: 2026-09-04 20:40 CST. This stays separate from the
// account plan because only administrator-pushed plans are shared across domains.
export interface DomainPlanItem {
  domainId: number
  domain: string
  billingMode: 'FREE' | 'MONTHLY' | 'PARTITION' | 'ADMIN_PUSH'
  status: 'ACTIVE' | 'PROTECTION' | 'DELETED'
  planName?: string
  freeRemainingSeconds: number
  freeExpiresAt?: string
  dailyAutoRenew: boolean
  paidUntil?: string
  monthlyAutoRenew: boolean
  protectionUntil?: string
  protectionStartedAt?: string | null
  deleteAt?: string | null
  deleteReason?: string | null
  suspensionReason?: string
  freeRenewAvailable: boolean
  switchBlocked: boolean
  switchBlockedMessage?: string
  partitionId?: number | null
  partitionDisplayName?: string | null
  allocationKey?: string | null
}

export interface DomainDeletionHistoryItem {
  eventId: number
  domainId: number
  domain: string
  cause: 'PLAN_EXPIRED_30_DAYS' | 'PARTITION_EXPIRED_90_DAYS' | 'USER_REQUEST'
  deletedAt: string
  source: 'AUTO' | 'MANUAL'
}

export interface DomainMonthlyPlan extends DomainPlanPartitionCatalogItem {
  description?: string
}

export interface DomainPlanOverview {
  domains: DomainPlanItem[]
  activePush?: ActivePushPlan | null
  plans: DomainMonthlyPlan[]
  partitions?: DomainPlanPartition[]
  dailyPointPrice: number
  serverNow: string
  serverTime: string
  deletionHistory: DomainDeletionHistoryItem[]
  deletionHistoryReadStatus: 'OK' | 'PARTIAL_MALFORMED_IGNORED'
  malformedDeletionHistoryCount: number
  // Daily billing guard V1.1 / 2026-09-10 CST / parent 15a7174ab:
  // response-only facts let the UI distinguish a real debit from a same-day toggle.
  dailyChargeApplied?: boolean
  dailyChargeDate?: string
}

export type DomainAccessPreviewSelection = DomainPackageSelection
  | { mode: 'FREE'; autoRenew?: boolean }
  | { mode: 'ADMIN_PUSH'; planId?: number }
  | { mode: 'PAID'; planId: number; autoRenew?: boolean };

export interface DomainAccessPreview {
  state: 'READY' | 'UPDATING';
  source: 'PLAN_TOPOLOGY' | 'DOMAIN_SCHEDULE';
  actual: boolean;
  addresses: string[];
  nodeCodes: string[];
  message: string;
}

export interface DomainPlanDeleteResult {
  domainId: number
  deleted: true
}

export const pointApi = {
  summary: () => httpClient.get<PointSummary>('/api/v1/console/points/summary'),
  signInStatus: () => httpClient.get<SignInStatus>('/api/v1/console/points/sign-in/status'),
  signIn: () => httpClient.post<SignInResult>('/api/v1/console/points/sign-in'),
  ledger: (params: Record<string, unknown>) => httpClient.get<PagedResponse<PointLedgerItem>>('/api/v1/console/points/ledger', {
    params: normalizeLedgerParams(params)
  }),
  orders: (params: Record<string, unknown>) => httpClient.get<PagedResponse<ConsumptionOrder>>('/api/v1/console/point-orders', {
    params
  }),
  catalog: () => httpClient.get<PlanCatalog>('/api/v1/console/plans/catalog'),
  purchasePlan: (planId: number, periodCount: number, idempotencyKey: string) =>
    httpClient.post<ConsumptionOrder>('/api/v1/console/plans/' + planId + '/purchase', {
      periodCount,
      idempotencyKey
    }),
  scheduleDowngrade: (planId: number, periodCount: number) =>
    httpClient.post<PendingPlan>('/api/v1/console/plans/' + planId + '/schedule-downgrade', {
      periodCount
    }),
  renewPlan: (periodCount: number, idempotencyKey: string) =>
    httpClient.post<ConsumptionOrder>('/api/v1/console/plans/current/renew', {
      periodCount,
      idempotencyKey
    }),
  setAutoRenew: (autoRenew: boolean) =>
    httpClient.put<CurrentPlan>('/api/v1/console/plans/current/auto-renew', { autoRenew }),
  domainPlans: () => httpClient.get<DomainPlanOverview>('/api/v1/console/domain-plans'),
  domainAccessPreview: (packageSelection: DomainAccessPreviewSelection, domainId?: number) =>
    httpClient.post<DomainAccessPreview>('/api/v1/console/domain-plans/access-preview', {
      packageSelection,
      ...(domainId ? { domainId } : {})
    }),
  renewFreeDomain: (domainId: number) =>
    httpClient.post<DomainPlanOverview>(`/api/v1/console/domain-plans/${domainId}/free-renew`),
  setDomainDailyAutoRenew: (domainId: number, enabled: boolean) =>
    httpClient.put<DomainPlanOverview>(`/api/v1/console/domain-plans/${domainId}/daily-auto-renew`, { enabled }),
  // Domain plan V1.0 / 2026-09-09 CST / parent 15a7174ab: preserve the
  // customer's explicit renewal choice instead of forcing every purchase on.
  purchaseDomainMonthly: (
    domainId: number,
    planId: number,
    periodCount: number,
    idempotencyKey: string,
    autoRenew = true
  ) =>
    httpClient.post<DomainPlanOverview>(`/api/v1/console/domain-plans/${domainId}/monthly`, {
      planId,
      periodCount,
      idempotencyKey,
      autoRenew
    }),
  assignDomainPackagePartition: (
    domainId: number,
    packageSelection: DomainPackageSelection,
    idempotencyKey: string
  ) => httpClient.post<DomainPlanOverview>(`/api/v1/console/domain-plans/${domainId}/monthly`, {
    ...(packageSelection.mode === 'EXISTING'
      ? { partitionId: packageSelection.partitionId }
      : {
          planId: packageSelection.planId,
          periodCount: packageSelection.periodCount,
          autoRenew: packageSelection.autoRenew
        }),
    idempotencyKey,
    packageSelection
  }),
  setDomainMonthlyAutoRenew: (domainId: number, enabled: boolean) =>
    httpClient.put<DomainPlanOverview>(`/api/v1/console/domain-plans/${domainId}/monthly-auto-renew`, { enabled }),
  pauseDomainPlan: (domainId: number) =>
    httpClient.post<DomainPlanOverview>(`/api/v1/console/domain-plans/${domainId}/pause`),
  resumeDomainPlan: (domainId: number) =>
    httpClient.post<DomainPlanOverview>(`/api/v1/console/domain-plans/${domainId}/resume`),
  renewPartition: (partitionId: number, periodCount: number, idempotencyKey: string) =>
    httpClient.post<DomainPlanPartition>(`/api/v1/console/domain-plans/partitions/${partitionId}/renew`, {
      periodCount,
      idempotencyKey
    }),
  setPartitionAutoRenew: (partitionId: number, enabled: boolean) =>
    httpClient.put<DomainPlanPartition>(`/api/v1/console/domain-plans/partitions/${partitionId}/auto-renew`, { enabled }),
  movePartitionDomains: (partitionId: number, targetPartitionId: number, domainIds: number[]) =>
    httpClient.post<DomainPlanPartition>(`/api/v1/console/domain-plans/partitions/${partitionId}/move`, {
      targetPartitionId,
      domainIds
    }),
  deleteDomainPlan: (domainId: number) =>
    httpClient.delete<DomainPlanDeleteResult>(`/api/v1/console/domain-plans/domains/${domainId}`),
  requestAdminPushCancelCode: () =>
    httpClient.post<{ sent: boolean; maskedEmail: string }>('/api/v1/console/domain-plans/admin-push/cancel-code'),
  cancelAdminPush: (emailCode: string) =>
    httpClient.post<DomainPlanOverview>('/api/v1/console/domain-plans/admin-push/cancel', { emailCode }),
  updatePendingPlan: (payload: { targetPlanId?: number; periodCount?: number; cancel?: boolean }) =>
    httpClient.put<PendingPlan>('/api/v1/console/plans/pending', payload),
  purchaseAddOn: (addOnId: number, periodCount: number, idempotencyKey: string) =>
    httpClient.post<ConsumptionOrder>('/api/v1/console/add-ons/' + addOnId + '/purchase', {
      periodCount,
      idempotencyKey
    }),
  notifications: (params: Record<string, unknown>) =>
    httpClient.get<NotificationPagedResponse>('/api/v1/console/notifications', { params }),
  markNotificationRead: (id: number) =>
    httpClient.post<{ read: true; id: number }>('/api/v1/console/notifications/' + id + '/read'),
  markAllNotificationsRead: () =>
    httpClient.post<{ readAll: true }>('/api/v1/console/notifications/read-all')
}
